// Nimny Course Map v2 coordinates.
// All coordinates are percentages inside the 16:9 map board.
// Background image is art-only; labels/statuses/clicks are rendered by code.
//
// Reading order (visual route on the board):
//   Week 1  → bottom-right (start)
//   Weeks 1→5 travel right-to-left along the bottom row
//   Week 5  → bottom-left
//   Week 6  → top-left (the route turns upward at the left edge)
//   Weeks 6→10 travel left-to-right along the top row
//   Week 10 → top-right, then the path continues UP to the Night Game gate
//   Night Game gate (door with crescent moon) → top-right corner

export const COURSE_MAP_VERSION = 'v2';

export const COURSE_WEEK_POSITIONS = {
  // Bottom row, RIGHT → LEFT (Weeks 1..5). Anchored on the glowing
  // bridge / island platforms at the bottom of the map artwork.
  'week-1':  { x: 84, y: 78 },
  'week-2':  { x: 67, y: 76 },
  'week-3':  { x: 50, y: 76 },
  'week-4':  { x: 31, y: 76 },
  'week-5':  { x: 12, y: 77 },
  // Top row, LEFT → RIGHT (Weeks 6..10). Anchored just below the
  // upper-row islands (houses, observatory, tower, etc.) — on the
  // grass/path area in front of the buildings, not on top of them.
  'week-6':  { x: 11, y: 43 },
  'week-7':  { x: 30, y: 39 },
  'week-8':  { x: 48, y: 38 },
  'week-9':  { x: 70, y: 40 },
  'week-10': { x: 87, y: 39 },
};

// Door / "Night Game" portal sits in the top-right corner. The anchor is
// placed just above the door art so that the pill label (transformed by
// CSS) ends up clearly ABOVE the door, never overlapping the arch.
export const COURSE_GAME_POSITION = { x: 93, y: 14 };

// Day dots are placed along the visible glowing path between consecutive
// weeks. Each entry interpolates from the week's own position toward the
// next week (or, for Week 10, toward the Night Game gate).
export const COURSE_DAY_TARGETS = {
  'week-1':  COURSE_WEEK_POSITIONS['week-2'],
  'week-2':  COURSE_WEEK_POSITIONS['week-3'],
  'week-3':  COURSE_WEEK_POSITIONS['week-4'],
  'week-4':  COURSE_WEEK_POSITIONS['week-5'],
  'week-5':  COURSE_WEEK_POSITIONS['week-6'],
  'week-6':  COURSE_WEEK_POSITIONS['week-7'],
  'week-7':  COURSE_WEEK_POSITIONS['week-8'],
  'week-8':  COURSE_WEEK_POSITIONS['week-9'],
  'week-9':  COURSE_WEEK_POSITIONS['week-10'],
  'week-10': COURSE_GAME_POSITION,
};

export const COURSE_SEGMENT_BENDS = {
  // Bottom row (R→L): gentle upward arcs.
  'week-1':   2.4,
  'week-2':  -2.6,
  'week-3':   2.2,
  'week-4':  -2.0,
  // Vertical-ish climb from W5 (bottom-left) to W6 (top-left).
  'week-5':  -6.0,
  // Top row (L→R): gentle downward arcs (mirror of bottom row).
  'week-6':   1.4,
  'week-7':  -1.8,
  'week-8':   1.6,
  'week-9':  -1.4,
  // Small flourish from W10 up to the Night Game gate.
  'week-10': -3.0,
};

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

export const getCourseMapPoint = (weekId) => COURSE_WEEK_POSITIONS[weekId];

export const getCourseMapGamePoint = () => COURSE_GAME_POSITION;

export const getCourseMapDayPoint = (weekId, dayNumber) => {
  const from = COURSE_WEEK_POSITIONS[weekId];
  if (!from) return { x: 50, y: 50 };

  const to = COURSE_DAY_TARGETS[weekId] || from;
  // Days 1..5 are placed at evenly spaced positions along the segment with
  // a small visible margin on both ends so neither the first nor the last
  // dot ever sits behind the current/next week's circle.
  const t = clamp(dayNumber / 7, 0, 1);
  const bend = COURSE_SEGMENT_BENDS[weekId] || 0;
  const midWave = Math.sin(Math.PI * t) * bend;

  return {
    x: from.x + (to.x - from.x) * t,
    y: from.y + (to.y - from.y) * t + midWave,
  };
};

export const getCourseMapPathPoints = () => {
  return [
    COURSE_WEEK_POSITIONS['week-1'],
    COURSE_WEEK_POSITIONS['week-2'],
    COURSE_WEEK_POSITIONS['week-3'],
    COURSE_WEEK_POSITIONS['week-4'],
    COURSE_WEEK_POSITIONS['week-5'],
    COURSE_WEEK_POSITIONS['week-6'],
    COURSE_WEEK_POSITIONS['week-7'],
    COURSE_WEEK_POSITIONS['week-8'],
    COURSE_WEEK_POSITIONS['week-9'],
    COURSE_WEEK_POSITIONS['week-10'],
    // Final flourish — the glowing path continues from Week 10 into the
    // Night Game gate so the route visibly ends at the door.
    COURSE_GAME_POSITION,
  ];
};
